import * as React from 'react';
import { useLocation, useNavigationType } from 'react-router-dom';

/**
 * The view each overview was last showing, by page key.
 *
 * Module memory rather than sessionStorage, which would also survive a reload: the whole
 * point of this is the trip out to a detail page and back, and a full refresh has to start
 * the page clean. Taking the browser away from this module clears it for free, so a reload
 * and a logout both reset it without any code of their own.
 */
const snapshots = new Map<string, unknown>();

/**
 * Overview -> detail -> overview, coming back to the filters the user left behind.
 *
 * Narrower on purpose than the Classes overview, which keeps its view for the whole
 * session: here the snapshot only survives the round trip to a detail page. Reaching the
 * list any other way - the nav menu, a link, a reload - starts it unfiltered, because that
 * is a new piece of work rather than a continuation of the last one.
 *
 * What counts as the way back is the same signal the Classes History page uses: the
 * browser's Back, or the `restoreFilters` state that the layout's Back button, the
 * breadcrumb links and the detail pages' own returns carry. A click on the nav menu is a
 * plain push and matches none of them.
 *
 * Call it above the state it seeds, and hand the current view back on every change:
 *
 *   const { restored, keep } = useFilterRestore<View>('instructors');
 *   const [filters, setFilters] = React.useState(restored?.filters ?? emptyFilters());
 *   React.useEffect(() => { keep({ filters }); }, [filters, keep]);
 *
 * Pagination is not part of this - `useRowRestore` already carries the page, along with
 * the scroll back to the row that was opened.
 *
 * @param pageKey Unique, stable name for this overview.
 */
export const useFilterRestore = <T,>(pageKey: string) => {
  const location = useLocation();
  const navType = useNavigationType();

  // A reload arrives as a POP too, so it reads as a way back - harmlessly, because it starts
  // this module empty and so has nothing to restore. Worth knowing because the obvious guard
  // against it, `location.key !== 'default'`, would also catch the genuine article: the first
  // page of a session keeps that key, so anyone landing straight on an overview would lose
  // their filters on the Back out of the first detail page they open.
  const isReturnTrip = navType === 'POP'
    || (location.state as { restoreFilters?: boolean } | null)?.restoreFilters === true;

  // Read, do not consume: under StrictMode a lazy initialiser runs twice, and a
  // destructive read would throw the snapshot away before the state below is seeded.
  const [restored] = React.useState<T | null>(() =>
    isReturnTrip ? ((snapshots.get(pageKey) as T | undefined) ?? null) : null
  );

  // Any arrival that is not a return trip drops what was kept, so a view from an earlier
  // visit cannot come back on some later, unrelated one. Declared before the caller's own
  // effect, so this cannot wipe the snapshot the first `keep` has just written.
  React.useEffect(() => {
    if (!isReturnTrip) snapshots.delete(pageKey);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  /** Hand the current view to the next mount. Cheap enough to call on every change. */
  const keep = React.useCallback((snapshot: T) => {
    snapshots.set(pageKey, snapshot);
  }, [pageKey]);

  return { restored, keep };
};
